import inMemoryJWT from './inMemoryJWT';

const apiAuth = process.env.REACT_APP_API_AUTH;
const apiBase = apiAuth ? apiAuth.replace(/\/auth\/?$/, '') : '';

const sanitizeReturnTo = returnTo => {
  if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) return '/';
  return returnTo;
};

const getCallbackParams = () => {
  const hashQueryIndex = window.location.hash.indexOf('?');
  if (hashQueryIndex >= 0) {
    return new URLSearchParams(window.location.hash.slice(hashQueryIndex + 1));
  }
  return new URLSearchParams(window.location.search);
};

const storeSession = ({tokens, user}) => {
  if (user) {
    localStorage.setItem('permissions', user.role);
    localStorage.setItem('auth', JSON.stringify({id: user.id, fullName: `${user.firstName} ${user.lastName}`}));
  }
  return inMemoryJWT.setToken(tokens.accessToken);
};

const hydrateIdentityFromApi = () => {
  const token = inMemoryJWT.getToken();
  if (!token || !apiBase) {
    return Promise.reject(new Error('Missing token or API base URL'));
  }

  const request = new Request(`${apiBase}/profile`, {
    method: 'GET',
    headers: new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }),
    credentials: 'include',
  });

  return fetch(request)
    .then(response => {
      if (!response.status || response.status < 200 || response.status >= 300) {
        throw new Error(response.statusText || 'Failed to load identity');
      }
      return response.json();
    })
    .then(user => {
      if (user) {
        localStorage.setItem('permissions', user.role);
        localStorage.setItem('auth', JSON.stringify({id: user.id, fullName: `${user.firstName} ${user.lastName}`}));
      }
      return user;
    });
};

export const getMicrosoftLoginUrl = (returnTo = '/') => {
  const params = new URLSearchParams({client: 'admin', returnTo: sanitizeReturnTo(returnTo)});
  return `${apiAuth}/microsoft/login?${params.toString()}`;
};

const authProvider = {
  login: ({username, password}) => {
    const request = new Request(`${apiAuth}/login`, {
      method: 'POST',
      body: JSON.stringify({email: username, password}),
      headers: new Headers({'Content-Type': 'application/json'}),
      credentials: 'include',
    });
    inMemoryJWT.setRefreshTokenEndpoint(`${apiAuth}/refresh-token-admin`);
    return fetch(request)
      .then(response => {
        if (!response.status || response.status < 200 || response.status >= 300) {
          throw new Error(response.statusText);
        }
        return response.json();
      })
      .then(storeSession);
  },

  handleCallback: () => {
    const params = getCallbackParams();
    const code = params.get('code');
    const returnTo = sanitizeReturnTo(params.get('returnTo'));

    if (!code) {
      return Promise.reject(new Error('Missing Microsoft SSO code'));
    }

    const request = new Request(`${apiAuth}/microsoft/exchange`, {
      method: 'POST',
      body: JSON.stringify({code}),
      headers: new Headers({'Content-Type': 'application/json'}),
      credentials: 'include',
    });

    inMemoryJWT.setRefreshTokenEndpoint(`${apiAuth}/refresh-token-admin`);
    return fetch(request)
      .then(response => {
        if (!response.status || response.status < 200 || response.status >= 300) {
          throw new Error(response.statusText);
        }
        return response.json();
      })
      .then(session => {
        storeSession(session);
        return {redirectTo: returnTo};
      });
  },

  getIdentity: () => {
    try {
      const userString = localStorage.getItem('auth');
      if (!userString) {
        return Promise.reject();
      }
      const {id, fullName, avatar} = JSON.parse(userString);
      return Promise.resolve({id, fullName, avatar});
    } catch (error) {
      return Promise.reject(error);
    }
  },

  logout: () => {
    const request = new Request(`${apiAuth}/logout`, {
      method: 'POST',
      credentials: 'include',
    });
    inMemoryJWT.ereaseToken();
    localStorage.removeItem('auth');

    return fetch(request)
      .then(() => '/login')
      .catch(() => '/login');
  },

  checkAuth: () => {
    const token = inMemoryJWT.getToken();
    if (!token) {
      inMemoryJWT.setRefreshTokenEndpoint(`${apiAuth}/refresh-token-admin`);
      return inMemoryJWT.getRefreshedToken().then(gotFreshToken => {
        if (gotFreshToken) {
          return hydrateIdentityFromApi().then(() => Promise.resolve());
        }
        return Promise.reject({redirectTo: '/login'});
      });
    }

    if (!localStorage.getItem('permissions') || !localStorage.getItem('auth')) {
      return hydrateIdentityFromApi().then(() => Promise.resolve());
    }

    return Promise.resolve();
  },

  checkError: error => {
    if (!error?.status || error.status === 401 || error.status === 403) {
      inMemoryJWT.ereaseToken();
      return Promise.reject('Unauthorized access');
    }
    return Promise.resolve();
  },

  getPermissions: () => {
    // return Promise.resolve('admin');
    const role = localStorage.getItem('permissions');
    return role ? Promise.resolve(role) : Promise.reject('No permission');
  },
};

export default authProvider;
